import clsx from "clsx";
import { ArrowRight, Check, GripVertical, Hash, Save, Shapes, X } from "lucide-react";
import { useState } from "react";

import { Motion, Presence } from "../../components/presence";
import { Button, IconButton, Kbd } from "../../components/ui";
import { k, t } from "../../i18n/i18n";

/** Prise en main de l'éditeur d'ordre de bataille, à sa première ouverture sur ce poste : quatre étapes courtes
 *  (l'arbre et le glisser, la palette, les numéros, l'enregistrement puis la force dans les Données initiales), dans
 *  une bulle en bas de l'arbre, à côté de la fiche, qui apparaît en douceur. « Passer » ou « Terminer » la referment pour de bon (retenu sur
 *  ce poste, sans gêne si le stockage est refusé). */

const DONE_KEY = "generex-odb-prise-en-main";

const STEPS = [
  {
    icon: GripVertical,
    title: k("L'arbre des unités"),
    text: k("Choisissez une unité pour ouvrir sa fiche. Glissez-la par sa poignée pour la déplacer, ou servez-vous du clavier : flèches, Entrée pour la renommer, + pour lui ajouter un subordonné."),
  },
  {
    icon: Shapes,
    title: k("La palette APP-6"),
    text: k("« Fonction APP-6 » ouvre les symboles terrestres OTAN : infanterie motorisée, mécanisée, blindés, appuis, soutien… avec leur qualificatif (parachutiste, montagne…). Raccourci : {key}."),
  },
  {
    icon: Hash,
    title: k("Les numéros du symbole"),
    text: k("En bas à gauche, le numéro de l'unité ; en bas à droite, celui de sa formation, repris d'office. Chiffres seulement, comme sur la carte."),
  },
  {
    icon: Save,
    title: k("Enregistrer, puis employer"),
    text: k("Enregistrez : l'ordre de bataille devient une force (ou un ennemi) à choisir dans les Données initiales, placée sur la carte comme celles fournies avec Generex."),
  },
];

function done(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === "1";
  } catch {
    return true; // stockage refusé : pas de bulle qui reviendrait à chaque ouverture
  }
}

/** `placement` : position de la bulle dans la zone de l'arbre (à côté de la fiche, jamais par-dessus : les numéros de
 *  l'étape 3 s'y saisissent). */
export function OdbTour({ placement = "right-4 bottom-4" }: { placement?: string }) {
  const [step, setStep] = useState<number | null>(() => (done() ? null : 0));
  const finish = () => {
    setStep(null);
    try {
      localStorage.setItem(DONE_KEY, "1");
    } catch {
      // stockage refusé : la bulle reste fermée pour cette ouverture
    }
  };
  const current = step === null ? null : STEPS[step];
  const last = step === STEPS.length - 1;
  return (
    <Presence show={current !== null && current !== undefined}>
      {current && step !== null && (
        <Motion
          origin="bottom"
          role="dialog"
          aria-label={t("Prise en main de l'éditeur")}
          // Les avis (« Numéros proposés »…) se posent à côté de la bulle ou au-dessus, jamais dessus ; la bulle passe
          // sous le voile de la liste et de la fiche en tiroir (z-30), qu'elle ne perce plus.
          data-notice-avoid=""
          className={clsx("absolute z-20 w-[320px] max-w-[calc(100%-32px)] rounded-xl border border-line bg-surface p-4 shadow-float", placement)}
        >
          <div className="flex items-start gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
              <current.icon size={16} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-meta text-ink-muted">{t("Étape {n} sur {total}", { n: step + 1, total: STEPS.length })}</p>
              <p className="text-ui leading-snug font-semibold text-ink">{t(current.title)}</p>
            </div>
            <IconButton size="xs" onClick={finish} title={t("Fermer")} aria-label={t("Fermer")}>
              <X size={14} />
            </IconButton>
          </div>
          <p className="mt-2 text-ui leading-relaxed text-ink-soft">
            {t(current.text, { key: "F2" }).split("F2").map((part, i, all) => (
              <span key={i}>
                {part}
                {i < all.length - 1 && <Kbd>F2</Kbd>}
              </span>
            ))}
          </p>
          <div className="mt-3 flex items-center justify-between gap-2">
            <Button size="sm" variant="ghost" onClick={finish}>
              {t("Passer")}
            </Button>
            <Button size="sm" highlight onClick={() => (last ? finish() : setStep(step + 1))}>
              {last ? <Check size={14} /> : <ArrowRight size={14} />} {last ? t("Terminer") : t("Suivant")}
            </Button>
          </div>
        </Motion>
      )}
    </Presence>
  );
}
